import { createFileRoute } from '@tanstack/react-router';
import { useTheme } from '../../../contexts/ThemeContext';

/**
 * Admin Overview - System overview and key metrics
 */
export const Route = createFileRoute('/_authenticated/admin/')({
  component: AdminOverview,
});

function AdminOverview() {
  const { theme } = useTheme();

  const colors = {
    dark: {
      bg: '#0a0a0f',
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(220, 38, 38, 0.3)',
      text: '#ffffff',
      textMuted: '#9ca3af',
      headerColor: '#ef4444',
      subtextColor: '#9ca3af'
    },
    light: {
      bg: '#ffffff',
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      text: '#111827',
      textMuted: '#6b7280',
      headerColor: '#dc2626',
      subtextColor: '#6b7280'
    }
  };

  const c = colors[theme];
  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            color: c.headerColor,
            marginBottom: '0.5rem'
          }}>
            Admin Overview
          </h1>
          <p style={{ color: c.subtextColor, fontSize: '1rem' }}>
            System-wide metrics and recent activity
          </p>
        </div>

        {/* System Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          <StatCard
            title="Total Users"
            value="0"
            subtitle="Active accounts"
            color={c.headerColor}
            theme={theme}
          />
          <StatCard
            title="Total Products"
            value="0"
            subtitle="Marketplace items"
            color={c.headerColor}
            theme={theme}
          />
          <StatCard
            title="Active Licenses"
            value="0"
            subtitle="Valid licenses"
            color={c.headerColor}
            theme={theme}
          />
          <StatCard
            title="Total Revenue"
            value="$0.00"
            subtitle="All-time earnings"
            color="#059669"
            theme={theme}
          />
        </div>

        {/* Activity Overview */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '1.5rem'
        }}>
          {/* Recent Activity */}
          <div style={{
            background: c.cardBg,
            border: `1px solid ${c.cardBorder}`,
            borderRadius: '0.5rem',
            padding: '1.5rem',
            backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none',
            boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              color: c.headerColor,
              marginBottom: '1rem'
            }}>
              Recent Activity
            </h2>
            <div style={{
              color: c.textMuted,
              fontSize: '0.875rem',
              textAlign: 'center',
              padding: '2rem'
            }}>
              No recent activity
            </div>
          </div>

          {/* System Health */}
          <div style={{
            background: c.cardBg,
            border: `1px solid ${c.cardBorder}`,
            borderRadius: '0.5rem',
            padding: '1.5rem',
            backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none',
            boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              color: c.headerColor,
              marginBottom: '1rem'
            }}>
              System Health
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <HealthItem label="API Status" status="operational" theme={theme} />
              <HealthItem label="Database" status="operational" theme={theme} />
              <HealthItem label="Storage" status="operational" theme={theme} />
              <HealthItem label="CDN" status="operational" theme={theme} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: string;
  subtitle: string;
  color: string;
  theme: 'dark' | 'light';
}

function StatCard({ title, value, subtitle, color, theme }: StatCardProps) {
  const colors = {
    dark: {
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(220, 38, 38, 0.3)',
      textMuted: '#9ca3af',
      subtitle: '#6b7280'
    },
    light: {
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      textMuted: '#6b7280',
      subtitle: '#9ca3af'
    }
  };

  const c = colors[theme];

  return (
    <div style={{
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      borderRadius: '0.5rem',
      padding: '1.5rem',
      backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none',
      boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)'
    }}>
      <h3 style={{
        fontSize: '0.875rem',
        fontWeight: '500',
        color: c.textMuted,
        marginBottom: '0.5rem'
      }}>
        {title}
      </h3>
      <p style={{
        fontSize: '1.875rem',
        fontWeight: 'bold',
        color: color,
        marginBottom: '0.25rem'
      }}>
        {value}
      </p>
      <p style={{
        fontSize: '0.75rem',
        color: c.subtitle
      }}>
        {subtitle}
      </p>
    </div>
  );
}

interface HealthItemProps {
  label: string;
  status: 'operational' | 'degraded' | 'down';
  theme: 'dark' | 'light';
}

function HealthItem({ label, status, theme }: HealthItemProps) {
  const statusColor = status === 'operational' ? '#10b981' :
                     status === 'degraded' ? '#f59e0b' : '#ef4444';

  const colors = {
    dark: {
      bg: 'rgba(220, 38, 38, 0.1)',
      labelColor: '#d1d5db',
      statusColor: statusColor
    },
    light: {
      bg: '#f9fafb',
      labelColor: '#4b5563',
      statusColor: statusColor
    }
  };

  const c = colors[theme];

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0.75rem',
      backgroundColor: c.bg,
      borderRadius: '0.375rem'
    }}>
      <span style={{ fontSize: '0.875rem', color: c.labelColor }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{
          width: '0.5rem',
          height: '0.5rem',
          borderRadius: '50%',
          backgroundColor: statusColor
        }}></span>
        <span style={{
          fontSize: '0.75rem',
          fontWeight: '500',
          color: c.statusColor,
          textTransform: 'capitalize'
        }}>
          {status}
        </span>
      </div>
    </div>
  );
}
